/* BSP für die Darstellung aufbereiten: Polygone mit Textur-UV und Lightmap-UV, Lightmap-Atlas, Umrisslinien. Keine DOM-Abhängigkeit. */
import type { Vec3 } from '../model/types.ts';
import type { BspFile, BspTexture } from './bsp-parse.ts';
import { faceVerts } from './bsp-parse.ts';

/** Lightmap-Block einer Fläche im Atlas (ms/mt: Ursprung in Lightmap-Einheiten zu 16 Texeln, off: Lage in b.lighting) */
interface LmBlock { w: number; h: number; ms: number; mt: number; off: number; x: number; y: number }

export interface ScenePoly {
  fi: number; model: number; tex: string; w: number; h: number; nz: number;
  verts: Vec3[]; S: number[]; T: number[]; special: boolean;
  lm: [number, number][] | null; block: LmBlock | null;
}
export interface LightAtlas { W: number; H: number; data: Uint8ClampedArray }
export interface BspScene {
  polys: ScenePoly[]; atlas: LightAtlas | null; lines: Float32Array;
  min: Vec3; max: Vec3; textures: Map<string, BspTexture>;
}

/** Szene für die Darstellung. Block 0 des Atlas ist ein weißer 4×4-Bereich für unbeleuchtete Flächen. */
export function scene(b: BspFile): BspScene {
  const polys: ScenePoly[] = [], blocks: LmBlock[] = [];
  const mod0 = b.models[0], fmodel = new Int32Array(b.faces.length);
  b.models.forEach((m, mi) => { for (let i = m.first; i < m.first + m.num && i < fmodel.length; i++) fmodel[i] = mi; });
  b.faces.forEach((f, fi) => {
    if (f.num < 3) return;
    const ti = b.texinfo[f.info]; if (!ti) return;
    const tx: { name: string; w: number; h: number } = b.textures[ti.tex] || { name: '?', w: 64, h: 64 };
    const verts = faceVerts(b, f), S: number[] = [], T: number[] = [];
    verts.forEach((p) => { S.push(p[0] * ti.s[0] + p[1] * ti.s[1] + p[2] * ti.s[2] + ti.so); T.push(p[0] * ti.t[0] + p[1] * ti.t[1] + p[2] * ti.t[2] + ti.to); });
    const special = (ti.flags & 1) !== 0;
    const poly: ScenePoly = { fi: fi, model: fmodel[fi], tex: tx.name, w: tx.w || 64, h: tx.h || 64, nz: f.nz, verts: verts, S: S, T: T, special: special, lm: null, block: null };
    if (!special && f.light >= 0 && f.style === 0) {
      const ms = Math.floor(Math.min.apply(null, S) / 16), Ms = Math.ceil(Math.max.apply(null, S) / 16);
      const mt = Math.floor(Math.min.apply(null, T) / 16), Mt = Math.ceil(Math.max.apply(null, T) / 16);
      const w = Ms - ms + 1, h = Mt - mt + 1;
      if (w > 0 && h > 0 && w <= 64 && h <= 64 && f.light + w * h * 3 <= b.lighting.length) { poly.block = { w: w, h: h, ms: ms, mt: mt, off: f.light, x: 0, y: 0 }; blocks.push(poly.block); }
    }
    polys.push(poly);
  });
  // Atlas packen (Regalpacken, 1 Pixel Rand)
  let atlas: LightAtlas | null = null;
  if (blocks.length) {
    const order = blocks.slice().sort((a, c) => c.h - a.h || c.w - a.w);
    let A = 512;
    const tryPack = (A: number): boolean => {
      let x = 6, y = 0, rowH = 0;
      for (const bl of order) {
        if (x + bl.w + 2 > A) { x = 0; y += rowH; rowH = 0; }
        if (y === 0 && x < 6) { x = 6; }
        bl.x = x + 1; bl.y = y + 1; x += bl.w + 2; rowH = Math.max(rowH, bl.h + 2);
        if (y + rowH > A) return false;
      }
      return true;
    };
    while (!tryPack(A) && A < 4096) A *= 2;
    if (A <= 4096 && tryPack(A)) {
      const data = new Uint8ClampedArray(A * A * 4);
      for (let y = 0; y < 4; y++) for (let x = 0; x < 4; x++) { const o = (y * A + x) * 4; data[o] = data[o + 1] = data[o + 2] = 255; data[o + 3] = 255; }
      for (const bl of blocks) {
        for (let y = -1; y <= bl.h; y++) for (let x = -1; x <= bl.w; x++) {
          const sx = Math.min(bl.w - 1, Math.max(0, x)), sy = Math.min(bl.h - 1, Math.max(0, y));
          const s = bl.off + (sy * bl.w + sx) * 3, o = ((bl.y + y) * A + bl.x + x) * 4;
          data[o] = b.lighting[s]; data[o + 1] = b.lighting[s + 1]; data[o + 2] = b.lighting[s + 2]; data[o + 3] = 255;
        }
      }
      atlas = { W: A, H: A, data: data };
      const size = A;
      polys.forEach((p) => {
        if (p.block) {
          const bl = p.block;
          p.lm = p.verts.map((_v, i): [number, number] => [(bl.x + (p.S[i] / 16 - bl.ms) + 0.5) / size, (bl.y + (p.T[i] / 16 - bl.mt) + 0.5) / size]);
        } else if (!p.special) p.lm = p.verts.map((): [number, number] => [2 / size, 2 / size]);
      });
    }
  }
  // Umrisse (doppelte Kanten einmal)
  const seen = new Set<number>(), ln: number[] = [];
  b.faces.forEach((f) => {
    for (let k = 0; k < f.num; k++) {
      const se = b.surf[f.first + k], e = Math.abs(se), a = b.edges[e * 2], c = b.edges[e * 2 + 1], key = a < c ? a * 65536 + c : c * 65536 + a;
      if (seen.has(key)) continue; seen.add(key);
      ln.push(b.verts[a * 3], b.verts[a * 3 + 1], b.verts[a * 3 + 2], b.verts[c * 3], b.verts[c * 3 + 1], b.verts[c * 3 + 2]);
    }
  });
  const tex = new Map<string, BspTexture>();
  b.textures.forEach((t) => { if (t.rgba && t.name) tex.set(t.name.toUpperCase(), t); });
  return { polys: polys, atlas: atlas, lines: new Float32Array(ln), min: mod0 ? mod0.min : [-512, -512, -512], max: mod0 ? mod0.max : [512, 512, 512], textures: tex };
}
